import React, { useState, useEffect } from 'react';
import { LineOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import { useSelector } from 'react-redux';
import { IndicatorShowExternal } from './modals/IndicatorShowExternal';

export default function SummaryDataTable({
  columns,
  data,
  loading = false,
  widths,
  calculatePercentage = false,
  className,
  decimalSymbol,
  decimalPlaces,
  colYearView = false,
  yearRange = [],
  yearOrder = "Descending",
  isDataFirstLastYear = false
}) {
  const [logData, setLogData] = useState(data);
  const indicatorsState = useSelector((state) => state.indicators);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 250,
    position: ["topLeft", "bottomLeft"],
    pageSizeOptions: ["10", "50", "100", "250"]
  });

  const defaultColumns = [];

  const yearColumns = yearRange.map(year => ({
    title: year.toString(),
    dataIndex: year.toString(),
    key: year.toString(),
    width: widths?.[year.toString()] || 'auto',
  }));

  if (yearOrder === "Descending") {
    yearColumns.reverse();
  }

  const finalColumns = [
    ...defaultColumns,
    ...columns
      .filter(col => !['id', 'indicator', 'year', 'world'].includes(col))
      .map(col => ({
        title: typeof col === 'string' ? col.charAt(0).toUpperCase() + col.slice(1) : col.title.charAt(0).toUpperCase() + col.title.slice(1),
        dataIndex: typeof col === 'string' ? col : col.dataIndex,
        key: typeof col === 'string' ? col : col.key,
        width: widths?.[col] || 'auto',
      })),
    ...colYearView ? yearColumns : []
  ];

  function formatValue(value) {
    if (typeof value === 'string' && isNaN(parseFloat(value))) {
        return value;
    }
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value; 

    let formattedValue;

    if (numValue < 100) {
        formattedValue = numValue.toFixed(1);
    } else if (numValue >= 100 && numValue < 1000) {
        formattedValue = Math.round(numValue).toString();
    } else if (numValue >= 1000) {
        formattedValue = Math.round(numValue).toLocaleString('en-US');
    }

    return formattedValue;
  }

  function formatDataset(data) {
    const excludedKeys = ['Year', 'ID', 'Unit', 'Indicator'];
    return data.map(item => {
        const formattedItem = {};
        Object.keys(item).forEach(key => {
            if (
                excludedKeys.some(exKey => key.toLowerCase() === exKey.toLowerCase()) ||
                key.toLowerCase().includes("year")
            ) {
                formattedItem[key] = item[key];
            } else {
                formattedItem[key] = formatValue(item[key]);
            }
        });
        return formattedItem;
    });
  }
  
  useEffect(() => {
    if (data) {
      let updatedData = data.map((element, index) => {
        element.key = index;
        if (columns.includes("indicator")) {
          element.indicator = indicatorsState.value[element.ind_code];
        }
        return element;
      });

      if (calculatePercentage) {
        updatedData = updatedData.map(element => {
          const percentage = ((element.d_value_last - element.d_value_first) / element.d_value_first) * 100;
          element.percentage = percentage.toFixed(2);
          return element;
        });
      }
      
      const formattedData = formatDataset(updatedData);
     
      if (isDataFirstLastYear) {
        setLogData(updatedData);
      } else {
        setLogData(formattedData);
      }
    }
  }, [data]);

  const handleIndicatorClick = (indicatorCode) => {
    setSelectedIndicator(indicatorsState.value[indicatorCode]);
    setModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedIndicator(null);
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const renderPercentageDifference = (percentageDifference) => {
    let color = 'black';
    let Icon = LineOutlined;
    if (percentageDifference > 0) {
      color = 'green';
      Icon = UpOutlined;
    } else if (percentageDifference < 0) {
      color = 'red';
      Icon = DownOutlined;
    }
    return percentageDifference !== 0 ? (
      <span style={{ color, fontWeight: "bold", float: "right" }}>
        ({percentageDifference}%) <Icon />
      </span>
    ) : (
      <span style={{ color, fontWeight: "bold", float: "right" }}>
        ({percentageDifference}%) <Icon />
      </span>
    );
  };

  function sortValue(colName, a, b, sortOrder = "ascend") {
    let direction = sortOrder === "ascend" ? 1 : -1;
    const isANull = a[colName] === null || a[colName] === undefined;
    const isBNull = b[colName] === null || b[colName] === undefined;

    if (isANull && isBNull) {
      return 0;
    } else if (isANull) {
      return direction;
    } else if (isBNull) {
      return -direction;
    }

    const valA = parseFloat(a[colName]);
    const valB = parseFloat(b[colName]);
    return valA - valB;
  }

  const transformedColumns = finalColumns.map(column => ({
    ...column,
    sorter: {
      compare: (a, b, sortOrder) => {
        if (column.dataIndex === "percentage") {
          return a.percentage - b.percentage;
        } else if (["d_value", "d_value_first", "d_value_last", "d_value_live", "d_value_test", "d_date"].includes(column.dataIndex)) {
          return sortValue(column.dataIndex, a, b, sortOrder);
        } else if (yearRange.includes(parseInt(column.dataIndex))) {
          return sortValue(column.dataIndex, a, b, sortOrder);
        } else if (typeof a[column.dataIndex] === "number") {
          return a[column.dataIndex] - b[column.dataIndex];
        } else {
          return a[column.dataIndex].localeCompare(b[column.dataIndex]);
        }
      }
    },
    filterMultiple: true,
    render: (text, record) => {
      const value = record[column.dataIndex];

      if (!isNaN(value) && !column.dataIndex.toLowerCase().includes('year') && !['Unit', 'World', 'Indicator', 'ID'].includes(column.dataIndex) && typeof value !== 'string') {
        const numericValue = parseFloat(value);
        return numericValue.toLocaleString();
      }

      switch (column.dataIndex) {
        case "id":
          return record.id;
        case "indicator":
          return (
            <a
              href="#"
              onClick={() => handleIndicatorClick(record.ind_code)}
              style={{ color: 'blue', textDecoration: 'underline' }}
            >
              {record.indicator}
            </a>
          );
        case "year":
          return record.year;
        default:
          return text;
      }
    }
  }));

  return (
    <div>
      <Table
        className={`dataTable ${className}`}
        columns={transformedColumns}
        dataSource={logData}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 'max-content', y: '80vh' }}
        rowClassName="table-row"
      />

        {selectedIndicator && (
          <IndicatorShowExternal
            visible={modalVisible}
            onCancel={handleModalCancel}
            indicator={selectedIndicator}
          />
        )}
    </div>
  );
}
